'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useLogger } from 'next-axiom';
import type { WishlistItemWithProduct } from '../../../components/pages/wishlist/client/wishlist-types';
import { WISHLIST_QUERY_KEYS } from '../wishlist-constants';
import type { CreateItemInput } from '../wishlist-proxy-client';
import { useWishlistSessionToken } from './use-wishlist-session-token';

/**
 * Hook for centralized wishlist cache invalidation
 * Provides consistent cache invalidation patterns across all wishlist mutations
 */
export function useWishlistCacheInvalidation() {
  const queryClient = useQueryClient();
  const { data: authToken } = useWishlistSessionToken();
  const log = useLogger().with({ location: 'useWishlistCacheInvalidation' });

  return {
    /**
     * Invalidate all wishlist-related queries
     * Use this when you're not sure what specific data changed
     */
    invalidateAll: () => {
      queryClient.invalidateQueries({ queryKey: [WISHLIST_QUERY_KEYS.ALL] });
      queryClient.invalidateQueries({ queryKey: [WISHLIST_QUERY_KEYS.LISTS] });
      queryClient.invalidateQueries({ queryKey: [WISHLIST_QUERY_KEYS.LIST] });
    },

    /**
     * Invalidate queries after list operations (create, update, delete)
     */
    invalidateAfterListChange: (listId?: string) => {
      queryClient.invalidateQueries({ queryKey: [WISHLIST_QUERY_KEYS.LISTS] });
      if (listId) {
        queryClient.invalidateQueries({
          queryKey: [WISHLIST_QUERY_KEYS.LIST, listId],
        });
      }
    },

    /**
     * Invalidate queries after item operations (add, remove, update)
     */
    invalidateAfterItemChange: (listId?: string) => {
      // Invalidate all items query (used in "All Items" tab)
      queryClient.invalidateQueries({
        queryKey: [WISHLIST_QUERY_KEYS.ALL, 'items'],
      });

      // Invalidate lists to update item counts
      queryClient.invalidateQueries({ queryKey: [WISHLIST_QUERY_KEYS.LISTS] });

      // If item belongs to a specific list, invalidate that list
      if (listId) {
        queryClient.invalidateQueries({
          queryKey: [WISHLIST_QUERY_KEYS.LIST, listId],
        });
      } else {
        // If we don't know which list, invalidate all individual lists
        queryClient.invalidateQueries({ queryKey: [WISHLIST_QUERY_KEYS.LIST] });
      }
    },

    /**
     * Remove specific list from cache (use after successful deletion)
     */
    removeListFromCache: (listId: string) => {
      queryClient.removeQueries({
        queryKey: [WISHLIST_QUERY_KEYS.LIST, listId],
      });
    },

    /**
     * Remove specific item from cache (use after successful deletion)
     */
    removeItemFromCache: (_itemId: string) => {
      // Note: We don't have a specific item query key pattern,
      // so we rely on invalidation instead
      queryClient.invalidateQueries({
        queryKey: [WISHLIST_QUERY_KEYS.ALL, 'items'],
      });
    },

    // Optimistic Update Functions

    /**
     * Optimistically add an item to a specific board
     * Returns previous data for potential rollback
     */
    optimisticallyAddItemToBoard: (item: CreateItemInput) => {
      if (!item.listId || !authToken) return null;

      const queryKey = [WISHLIST_QUERY_KEYS.LIST, item.listId, authToken];

      // Snapshot previous data
      const previousData = queryClient.getQueryData(queryKey);

      // Create temp ID for optimistic update
      const tempId = `optimistic-${item.productId}-${item.variantId || 'null'}-${Date.now()}`;

      // Optimistically update the board
      queryClient.setQueryData(queryKey, (oldData: unknown) => {
        if (oldData && typeof oldData === 'object' && 'items' in oldData) {
          const boardData = oldData as { items?: unknown[] };
          return {
            ...boardData,
            items: [
              ...(boardData.items || []),
              {
                id: tempId,
                productId: item.productId,
                variantId: item.variantId,
                quantity: item.quantity,
                listId: item.listId,
              },
            ],
          };
        }
        return oldData;
      });

      return { previousData, queryKey, tempId };
    },

    /**
     * Revert an optimistic update (use in onError)
     */
    revertOptimisticUpdate: (rollbackData: {
      previousData: unknown;
      queryKey: unknown[];
    }) => {
      if (rollbackData?.previousData && rollbackData?.queryKey) {
        queryClient.setQueryData(
          rollbackData.queryKey,
          rollbackData.previousData
        );
      }
    },

    /**
     * Cancel queries for specific board to prevent race conditions
     */
    cancelBoardQueries: async (listId: string) => {
      if (!authToken) return;
      await queryClient.cancelQueries({
        queryKey: [WISHLIST_QUERY_KEYS.LIST, listId, authToken],
      });
    },

    /**
     * Optimistically add an item to "All Items" (no specific board)
     * Returns previous data for potential rollback
     */
    optimisticallyAddItemToAllItems: (item: CreateItemInput) => {
      if (!authToken) return null;

      const queryKey = [WISHLIST_QUERY_KEYS.ALL, 'items'];

      // Snapshot previous data
      const previousData = queryClient.getQueryData(queryKey);

      // Create temp ID for optimistic update
      const tempId = `optimistic-${item.productId}-${item.variantId || 'null'}-${Date.now()}`;

      // Optimistically update the all items cache
      queryClient.setQueryData(queryKey, (oldData: unknown[] = []) => {
        return [
          ...oldData,
          {
            id: tempId,
            productId: item.productId,
            variantId: item.variantId,
            quantity: item.quantity,
            listId: item.listId, // Will be undefined/null for "All Items"
          },
        ];
      });

      return { previousData, queryKey, tempId };
    },

    /**
     * Cancel all items queries to prevent race conditions
     */
    cancelAllItemsQueries: async () => {
      await queryClient.cancelQueries({
        queryKey: [WISHLIST_QUERY_KEYS.ALL, 'items'],
      });
    },

    /**
     * Synchronously add item to board for immediate UI feedback
     * This bypasses all async operations for instant updates
     */
    instantlyAddItemToBoard: (
      item: CreateItemInput,
      existingItemWithProduct?: WishlistItemWithProduct
    ) => {
      if (!item.listId || !authToken) {
        log.error('Missing required data for instant board update', {
          hasListId: !!item.listId,
          hasAuthToken: !!authToken,
        });
        return null;
      }

      const queryKey = [WISHLIST_QUERY_KEYS.LIST, item.listId, authToken];

      // Get current data
      const currentData = queryClient.getQueryData(queryKey);

      if (
        currentData &&
        typeof currentData === 'object' &&
        'items' in currentData
      ) {
        const boardData = currentData as { items?: unknown[] };
        const tempId = `instant-${item.productId}-${item.variantId || 'null'}-${Date.now()}`;

        // Create optimistic item with full product data if available
        const optimisticItem = existingItemWithProduct
          ? {
              // Use the existing item's product data for immediate rich display
              ...existingItemWithProduct,
              id: tempId,
              listId: item.listId,
              quantity: item.quantity,
            }
          : {
              // Fallback to basic item data
              id: tempId,
              productId: item.productId,
              variantId: item.variantId,
              quantity: item.quantity,
              listId: item.listId,
            };

        // Immediately set the new data - completely synchronous
        const newData = {
          ...boardData,
          items: [...(boardData.items || []), optimisticItem],
        };

        queryClient.setQueryData(queryKey, newData);
        return { previousData: currentData, queryKey, tempId };
      }

      return null;
    },

    /**
     * Replace optimistic items with real server data without invalidation
     * This prevents jank by updating items in place
     */
    replaceOptimisticItemsInBoard: (listId: string) => {
      if (!authToken) return;

      const queryKey = [WISHLIST_QUERY_KEYS.LIST, listId, authToken];

      // Silently refetch the board data in the background
      queryClient
        .refetchQueries({
          queryKey,
          type: 'active',
        })
        .catch(() => {
          // If refetch fails, fall back to invalidation
          queryClient.invalidateQueries({ queryKey });
        });
    },
  };
}
